/**
 * useAiOverview — manages AI Overview streaming state.
 *
 * Activated when URL contains `ai_query=...`.
 * Streams through browseWithAi, builds summary text via typewriter,
 * applies filters to the URL, and dispatches annotations to Redux.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

import { parse, stringify } from '../../../util/urlHelpers';
import { createResourceLocatorString } from '../../../util/routes';
import { browseWithAi } from '../../../util/api';
import { getSearchPageResourceLocatorStringParams } from '../SearchPage.shared';
import useTypewriter from '../../../hooks/useTypewriter';
import { completeAiTool, finalizeAiTools, startAiTool } from '../../../util/aiToolProgress';
import { sanitizeAiOverviewBaseParams } from '../../../util/aiOverviewSearch';

/**
 * @param {Object} deps
 * @param {Object} deps.location — react-router location
 * @param {Object} deps.history — react-router history
 * @param {Object} deps.routeConfiguration — route configuration
 * @param {Array}  deps.listings — current search result listings (from Redux)
 */
export default function useAiOverview({ location, history, routeConfiguration, listings }) {
  const queryParams = useMemo(() => parse(location.search), [location.search]);
  const aiQuery = queryParams.ai_query;
  const isAiQueryMode = !!aiQuery;

  // ── State ──────────────────────────────────────────────────────────
  const [aiOverviewActive, setAiOverviewActive] = useState(false);
  const [aiOverviewContent, setAiOverviewContent] = useState('');
  const [aiOverviewFilters, setAiOverviewFilters] = useState(null);
  const [aiOverviewComplete, setAiOverviewComplete] = useState(false);
  const [aiOverviewError, setAiOverviewError] = useState(null);
  const [aiOverviewAnnotations, setAiOverviewAnnotations] = useState([]);
  const [aiOverviewIntent, setAiOverviewIntent] = useState(null);
  const [aiOverviewConfidence, setAiOverviewConfidence] = useState(null);
  const [aiOverviewExplanation, setAiOverviewExplanation] = useState(null);
  const [aiOverviewMode, setAiOverviewMode] = useState(null);
  const [aiOverviewExcluded, setAiOverviewExcluded] = useState(null);
  const [aiToolSteps, setAiToolSteps] = useState([]);
  // Tracks whether the AI has called `respond` — shimmer persists until this flips
  const [hasRespondStarted, setHasRespondStarted] = useState(false);

  const aiOverviewQueryRef = useRef(null);
  const aiOverviewAbortRef = useRef(null);
  const pendingFiltersRef = useRef(null);
  const pendingOverviewRef = useRef({
    filters: null,
    annotations: [],
    intent: null,
    confidence: null,
    explanation: null,
    mode: null,
    excludedSummary: null,
  });
  const typewriter = useTypewriter();

  // Sync typewriter output to state
  useEffect(() => {
    setAiOverviewContent(typewriter.displayText);
  }, [typewriter.displayText]);

  // ── Apply browse filters to URL ────────────────────────────────────
  const applyBrowseFiltersToUrl = useCallback(
    filters => {
      const current = parse(location.search);
      const merged = {
        ...sanitizeAiOverviewBaseParams(current),
        ai_query: aiQuery,
        keywords: aiQuery,
        ...filters,
      };
      const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
        routeConfiguration,
        location
      );
      history.push(createResourceLocatorString(routeName, routeConfiguration, pathParams, merged));
    },
    [aiQuery, location, routeConfiguration, history]
  );

  // ── Build listing context for AI ───────────────────────────────────
  const buildListingsContext = useCallback(() => {
    const currentKeywords = typeof queryParams.keywords === 'string' ? queryParams.keywords.trim() : '';
    if (!aiQuery || currentKeywords !== aiQuery.trim()) {
      return [];
    }

    const currentListings = listings || [];
    if (currentListings.length === 0) return [];
    return currentListings.slice(0, 12).map(l => {
      const { title, price, publicData } = l.attributes || {};
      const author = l.author?.attributes?.profile;
      return {
        id: l.id?.uuid,
        title: title || 'Untitled',
        price: price?.amount || 0,
        condition: publicData?.condition_id || 'N/A',
        category: publicData?.categoryLevel2 || publicData?.categoryLevel1 || '',
        description: (publicData?.description || '').slice(0, 120),
        seller: author?.displayName || 'Unknown',
      };
    });
  }, [aiQuery, listings, queryParams.keywords]);

  // ── Scroll to a specific listing and highlight ─────────────────────
  const handleScrollToListing = useCallback(listingId => {
    const el = document.querySelector(`[data-listing-id="${listingId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('aiScrollHighlight');
      setTimeout(() => el.classList.remove('aiScrollHighlight'), 2000);
    }
  }, []);

  // ── Navigate to new AI search from suggestion pill ─────────────────
  const handleAiSuggestion = useCallback(
    suggestion => {
      const current = parse(location.search);
      const nextSearch = {
        ai_query: suggestion,
      };

      if (current.ai_overview) {
        nextSearch.ai_overview = current.ai_overview;
      }

      history.push(`/s?${stringify(nextSearch)}`);
    },
    [history, location.search]
  );

  // ── Quick action from TopPick cards ────────────────────────────────
  const handleQuickAction = useCallback(
    (action, listingId) => {
      handleScrollToListing(listingId);
    },
    [handleScrollToListing]
  );

  const flushPendingOverview = useCallback(() => {
    const pending = pendingOverviewRef.current;

    if (pending.filters) {
      setAiOverviewFilters(pending.filters);
    }

    setAiOverviewAnnotations(pending.annotations || []);
    setAiOverviewIntent(pending.intent || null);
    setAiOverviewConfidence(pending.confidence ?? null);
    setAiOverviewExplanation(pending.explanation || null);
    setAiOverviewMode(pending.mode || null);
    setAiOverviewExcluded(pending.excludedSummary || null);

  }, []);

  // ── Trigger browseWithAi when ai_query is detected ─────────────────
  useEffect(() => {
    if (!isAiQueryMode) {
      if (aiOverviewActive && aiOverviewAbortRef.current) {
        aiOverviewAbortRef.current();
        setAiOverviewActive(false);
        typewriter.flush();
        setAiToolSteps([]);
      }
      return;
    }

    if (aiOverviewQueryRef.current === aiQuery) return;
    aiOverviewQueryRef.current = aiQuery;

    // Seed keywords so Sharetribe shows baseline results
    const current = parse(location.search);
    if (!current.keywords) {
      history.replace(`${location.pathname}?${stringify({ ...current, keywords: aiQuery })}`);
    }

    // Reset state for new query
    setAiOverviewActive(true);
    setAiOverviewContent('');
    setAiOverviewFilters(null);
    setAiOverviewComplete(false);
    setAiOverviewError(null);
    setAiOverviewAnnotations([]);
    setAiOverviewIntent(null);
    setAiOverviewConfidence(null);
    setAiOverviewExplanation(null);
    setAiOverviewMode(null);
    setAiOverviewExcluded(null);
    setAiToolSteps([]);
    setHasRespondStarted(false);
    pendingFiltersRef.current = null;
    pendingOverviewRef.current = {
      filters: null,
      annotations: [],
      intent: null,
      confidence: null,
      explanation: null,
      mode: null,
      excludedSummary: null,
    };
    typewriter.reset();

    if (aiOverviewAbortRef.current) {
      aiOverviewAbortRef.current();
    }

    const listingsContext = buildListingsContext();

    const abort = browseWithAi(aiQuery, {
      context: {
        current_page: 'search',
        source: 'search_bar',
        listings: listingsContext,
      },
      callbacks: {
        onFiltersApplied: (filters, summary) => {
          pendingOverviewRef.current = {
            ...pendingOverviewRef.current,
            filters: { filters, summary },
          };
          pendingFiltersRef.current = filters;
        },
        onToolStart: tool => {
          setAiToolSteps(prev => startAiTool(prev, tool));

          if (tool === 'respond') {
            setHasRespondStarted(true);
            flushPendingOverview();
          }
        },
        onToolResult: tool => {
          setAiToolSteps(prev => completeAiTool(prev, tool));
        },
        onAnnotations: annotations => {
          pendingOverviewRef.current = {
            ...pendingOverviewRef.current,
            annotations: annotations || [],
          };
        },
        onMetadata: event => {
          pendingOverviewRef.current = {
            ...pendingOverviewRef.current,
            intent: event.intent || pendingOverviewRef.current.intent,
            excludedSummary: event.excluded_summary || pendingOverviewRef.current.excludedSummary,
            confidence:
              event.confidence != null ? event.confidence : pendingOverviewRef.current.confidence,
            explanation: event.explanation || pendingOverviewRef.current.explanation,
            mode: event.mode || pendingOverviewRef.current.mode,
          };
        },
        onContent: text => {
          typewriter.append(text);
        },
        onDone: event => {
          typewriter.flush();
          setAiToolSteps(prev => finalizeAiTools(prev));
          setAiOverviewActive(false);
          setAiOverviewComplete(true);
          pendingOverviewRef.current = {
            ...pendingOverviewRef.current,
            intent: event?.intent || pendingOverviewRef.current.intent,
            excludedSummary: event?.excluded_summary || pendingOverviewRef.current.excludedSummary,
            confidence:
              event?.confidence != null ? event.confidence : pendingOverviewRef.current.confidence,
            explanation: event?.explanation || pendingOverviewRef.current.explanation,
            mode: event?.mode || pendingOverviewRef.current.mode,
          };
          flushPendingOverview();
          if (pendingFiltersRef.current) {
            applyBrowseFiltersToUrl(pendingFiltersRef.current);
            pendingFiltersRef.current = null;
          }
        },
        onError: error => {
          typewriter.flush();
          setAiToolSteps(prev => finalizeAiTools(prev));
          setAiOverviewActive(false);
          setAiOverviewError(typeof error === 'string' ? error : 'Something went wrong');
        },
      },
    });

    aiOverviewAbortRef.current = abort;

    return () => {
      if (aiOverviewAbortRef.current) {
        aiOverviewAbortRef.current();
      }
      typewriter.reset();
    };
  }, [isAiQueryMode, aiQuery]);

  // ── Dismiss AI Overview ────────────────────────────────────────────
  const handleDismissAiOverview = useCallback(() => {
    pendingFiltersRef.current = null;
    setAiToolSteps([]);
    setHasRespondStarted(false);
    setAiOverviewContent('');
    setAiOverviewComplete(false);
    setAiOverviewActive(false);
    setAiOverviewFilters(null);
    setAiOverviewAnnotations([]);
    setAiOverviewError(null);
    setAiOverviewIntent(null);
    setAiOverviewConfidence(null);
    setAiOverviewExplanation(null);
    setAiOverviewMode(null);
    setAiOverviewExcluded(null);
    pendingOverviewRef.current = {
      filters: null,
      annotations: [],
      intent: null,
      confidence: null,
      explanation: null,
      mode: null,
      excludedSummary: null,
    };
    typewriter.reset();
    aiOverviewQueryRef.current = null;

    const current = parse(location.search);
    delete current.ai_query;
    const newSearch = stringify(current);
    history.replace(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`);
  }, [location.search, location.pathname, history, typewriter]);

  return {
    aiQuery,
    isAiQueryMode,
    aiOverviewActive,
    aiOverviewContent,
    aiOverviewFilters,
    aiOverviewComplete,
    aiOverviewError,
    aiOverviewAnnotations,
    aiOverviewIntent,
    aiOverviewConfidence,
    aiOverviewExplanation,
    aiOverviewMode,
    aiOverviewExcluded,
    aiToolSteps,
    hasRespondStarted,
    handleDismissAiOverview,
    handleScrollToListing,
    handleAiSuggestion,
    handleQuickAction,
  };
}
